export const images = {
  logo: "/site/logo.webp",
  hero: "/site/hero.webp",
  textiles: "/categories/technical-textiles.webp",
  flexible: "/categories/flexible-packaging.webp",
  rigid: "/categories/rigid-packaging.webp",
  bottling: "/site/bottling-line.webp",
  quality: "/site/quality-inspection.webp",
};
export const productImages = {
  uvGreenShadeNet: "/products/uv-green-shade-net.webp",
  multipurposeTentNet: "/products/multipurpose-tent-net.webp",
  constructionNet: "/products/construction-net.webp",
  agriculturalNet: "/products/agricultural-net.webp",
  carpetNet: "/products/carpet-net.webp",
  collationShrinkWrap: "/products/collation-shrink-wrap.webp",
  hmLdRolls: "/products/hm-ld-rolls.webp",
  heavyDutyGarbageBags: "/products/heavy-duty-garbage-bags.webp",
  petPreforms: "/products/pet-preforms.webp",
  closuresCaps: "/products/closures-caps.webp",
};

export type Spec = { label: string; value: string };
export type Division = { id: string; code: string; title: string; shortTitle: string; headline: string; description: string; image: string; specs: Spec[] };

export const divisions: Division[] = [
  {
    id: "technical-textiles",
    code: "DIV-01 // TXT",
    title: "Technical Textiles & Agrotech",
    shortTitle: "Technical Textiles",
    headline: "Engineered mesh for real-world load.",
    description: "High-tenacity PP and HDPE structures for agricultural, construction and infrastructure applications where weather stability and tensile reliability are non-negotiable.",
    image: images.textiles,
    specs: [
      { label: "Tensile strength", value: "Up to 55 kN/m" },
      { label: "GSM range", value: "70 – 650 GSM" },
      { label: "Weather stabilization", value: "5000+ hours accelerated weathering" },
      { label: "Custom width", value: "Up to 8 metres" },
    ],
  },
  {
    id: "flexible-packaging",
    code: "DIV-02 // FLX",
    title: "Packtech & Flexible Packaging",
    shortTitle: "Flexible Packaging",
    headline: "Barrier films that hold the line.",
    description: "Multi-layer blown films, shrink solutions and heavy-duty liners tuned for moisture control, clean handling and dependable high-volume conversion.",
    image: images.flexible,
    specs: [
      { label: "Film thickness", value: "25 – 250 micron" },
      { label: "Co-extrusion", value: "Up to 9 layers" },
      { label: "Print precision", value: "Up to 10 colours" },
      { label: "Sealing range", value: "-20° to 120°C" },
    ],
  },
  {
    id: "rigid-packaging",
    code: "DIV-03 // RGD",
    title: "Rigid Packaging & Closures",
    shortTitle: "Rigid Packaging",
    headline: "Containment built for impact.",
    description: "Precision PET and HDPE packaging components with controlled wall geometry, dependable closure fit and the kind of repeatability scaled procurement requires.",
    image: images.rigid,
    specs: [
      { label: "Volume spectrum", value: "250 ml – 250 L" },
      { label: "Drop impact", value: "2.2 m certified" },
      { label: "Hydraulic pressure", value: ">250 kPa" },
      { label: "Resin grade", value: "Virgin HDPE / PET" },
    ],
  },
  {
    id: "bottling",
    code: "DIV-04 // BTL",
    title: "Bottling & Liquid Packaging",
    shortTitle: "Bottling Automation",
    headline: "High-speed liquid lines, under control.",
    description: "Synchronized filling, capping and labelling systems for liquid products that demand clean throughput, traceability and precision at every cycle.",
    image: images.bottling,
    specs: [
      { label: "Line throughput", value: "120 – 350 BPM" },
      { label: "Filling accuracy", value: "±0.25% volume" },
      { label: "Viscosity handling", value: "1 – 50,000 cPs" },
      { label: "Serialization", value: "2D track & trace" },
    ],
  },
];

export type Product = {
  slug: string;
  name: string;
  category: string;
  divisionId: string;
  image: string;
  summary: string;
  overview: string;
  specs: Spec[];
  customization: string[];
  applications: string[];
  relatedSlugs: string[];
};

export const products: Product[] = [
  {
    slug: "green-shade-net",
    name: "Green Shade Net",
    category: "Technical Textiles & Agrotech",
    divisionId: "technical-textiles",
    image: productImages.uvGreenShadeNet,
    summary: "Stable, breathable shade structures for protected cultivation and commercial nurseries.",
    overview: "A high-tenacity knitted shade fabric engineered for controlled light transmission, airflow and dependable outdoor service. The construction stays consistent across repeat runs, widths and seasonal demand.",
    specs: [{ label: "Shade factor", value: "35% – 90%" }, { label: "Width", value: "Up to 8 m" }, { label: "Outdoor life", value: "5000+ hours accelerated weathering" }, { label: "Material", value: "Weather-stabilized HDPE" }],
    customization: ["Shade percentage and colour", "Roll length and edge finishing", "Eyelets, tapes and stitched hems"],
    applications: ["Greenhouses", "Nursery tunnels", "Orchard protection", "Warehouse screening"],
    relatedSlugs: ["agricultural-net", "construction-net"],
  },
  {
    slug: "multipurpose-tent-net",
    name: "Multipurpose Tent Net",
    category: "Technical Textiles & Agrotech",
    divisionId: "technical-textiles",
    image: productImages.multipurposeTentNet,
    summary: "Lightweight woven protection for event, utility and temporary-structure programs.",
    overview: "A durable, easy-to-handle netting platform for temporary structures and field use. Designed around repeatable rolls, clean finishing and straightforward installation at scale.",
    specs: [{ label: "Mesh construction", value: "Warp knitted" }, { label: "Basis weight", value: "90 – 220 GSM" }, { label: "Roll width", value: "1 – 6 m" }, { label: "Finish", value: "Cut / hem / eyelet" }],
    customization: ["Colour matching", "Printed identification bands", "Reinforced perimeter and tie points"],
    applications: ["Tents and canopies", "Temporary partitions", "Event infrastructure", "Utility covers"],
    relatedSlugs: ["green-shade-net", "construction-net"],
  },
  {
    slug: "construction-net",
    name: "Construction Net",
    category: "Technical Textiles & Agrotech",
    divisionId: "technical-textiles",
    image: productImages.constructionNet,
    summary: "High-visibility perimeter protection for active construction and infrastructure sites.",
    overview: "A rugged safety net platform created for jobsite screening, debris control and edge protection. Stable geometry and disciplined finishing help contractors deploy with confidence.",
    specs: [{ label: "Tensile strength", value: "Up to 55 kN/m" }, { label: "GSM range", value: "100 – 650 GSM" }, { label: "Weather package", value: "Outdoor stabilized" }, { label: "Colour", value: "High-visibility options" }],
    customization: ["Site-specific dimensions", "Reinforced seams", "Colour and branding bands"],
    applications: ["Scaffolding", "Facade screening", "Bridge works", "Roadside protection"],
    relatedSlugs: ["multipurpose-tent-net", "agricultural-net"],
  },
  {
    slug: "agricultural-net",
    name: "Agricultural Net",
    category: "Technical Textiles & Agrotech",
    divisionId: "technical-textiles",
    image: productImages.agriculturalNet,
    summary: "Protective knitted netting for crop, nursery and controlled-growing environments.",
    overview: "A protective knitted net made from durable plastic materials such as Weather-stabilized HDPE. It helps reduce excessive sunlight, wind and wind-borne dust while maintaining airflow and creating a more controlled growing environment.",
    specs: [{ label: "Material", value: "Weather-stabilized HDPE" }, { label: "Structure", value: "Open mesh with partial light penetration" }, { label: "Protection", value: "Shade, wind and dust exposure management" }, { label: "Outdoor performance", value: "Weather durability and reduced brittleness" }],
    customization: ["Select the mesh size and density", "Choose the shade level", "Match the crop, climate and installation environment"],
    applications: ["Vegetable and fruit cultivation", "Nurseries and greenhouses", "Orchards and vineyards", "Floriculture", "Open fields and farms", "Protected cultivation and windbreak areas"],
    relatedSlugs: ["green-shade-net", "carpet-net"],
  },
  {
    slug: "carpet-net",
    name: "Carpet Net",
    category: "Technical Textiles & Agrotech",
    divisionId: "technical-textiles",
    image: productImages.carpetNet,
    summary: "Large-format net floor matting for caterers and temporary functions.",
    overview: "Carpet Net is a practical net floor-matting product made for caterers setting up functions. It provides a large, easy-to-handle surface for temporary event areas and is supplied in the specified 16 × 32 feet format with multiple colour options.",
    specs: [{ label: "Primary use", value: "Floor matting for caterers" }, { label: "Dimensions", value: "16 × 32 feet" }, { label: "Colour options", value: "Multiple colours" }, { label: "Format", value: "Large rectangular net mat" }],
    customization: ["Select from multiple colour options", "Confirm the 16 × 32 feet format", "Plan quantity around the function layout"],
    applications: ["Caterer functions", "Temporary event floor matting", "Outdoor gatherings", "Function and event areas"],
    relatedSlugs: ["multipurpose-tent-net", "green-shade-net"],
  },
  {
    slug: "collation-shrink-wrap",
    name: "Collation Shrink Wrap",
    category: "Packtech & Flexible Packaging",
    divisionId: "flexible-packaging",
    image: productImages.collationShrinkWrap,
    summary: "Clean, high-clarity bundling film for fast-moving beverage and FMCG lines.",
    overview: "Machine-ready shrink film engineered for consistent sealing, controlled shrink and clear shelf presentation across high-speed collation formats.",
    specs: [{ label: "Thickness", value: "35 – 120 micron" }, { label: "Shrink ratio", value: "Custom MD / TD" }, { label: "Clarity", value: "High-clarity options" }, { label: "Printing", value: "Up to 10 colours" }],
    customization: ["Film gauge and shrink profile", "Printed roll stock", "Perforation and pack format"],
    applications: ["Beverage multipacks", "FMCG bundling", "Industrial grouping", "Retail-ready packs"],
    relatedSlugs: ["hm-ld-rolls", "heavy-duty-garbage-bags"],
  },
  {
    slug: "hm-ld-rolls",
    name: "HM & LD Rolls",
    category: "Packtech & Flexible Packaging",
    divisionId: "flexible-packaging",
    image: productImages.hmLdRolls,
    summary: "Reliable high- and low-density film rolls for conversion, liners and industrial handling.",
    overview: "A broad rollstock platform balancing puncture resistance, seal performance and clean conversion for industrial, retail and agricultural packaging programs.",
    specs: [{ label: "Thickness", value: "25 – 250 micron" }, { label: "Film type", value: "HM / LDPE" }, { label: "Roll width", value: "Up to 2.4 m" }, { label: "Core", value: "76 / 152 mm" }],
    customization: ["Gauge and width", "Anti-static or slip packages", "Printed or plain finish"],
    applications: ["Liners", "Inner bags", "Pallet covers", "Conversion stock"],
    relatedSlugs: ["collation-shrink-wrap", "heavy-duty-garbage-bags"],
  },
  {
    slug: "heavy-duty-garbage-bags",
    name: "Heavy-Duty Garbage Bags & Liners",
    category: "Packtech & Flexible Packaging",
    divisionId: "flexible-packaging",
    image: productImages.heavyDutyGarbageBags,
    summary: "Tear-resistant liners for municipal, industrial and institutional waste streams.",
    overview: "Heavy-duty bags and liners produced for demanding collection, transport and containment environments, with disciplined sealing and practical packing formats.",
    specs: [{ label: "Material", value: "Recycled / virgin blends" }, { label: "Gauge", value: "40 – 180 micron" }, { label: "Capacity", value: "20 – 500 litres" }, { label: "Seal", value: "Star / flat seal" }],
    customization: ["Bag dimensions", "Colour and opacity", "Printed identification", "Pack count"],
    applications: ["Municipal waste", "Industrial plants", "Healthcare facilities", "Commercial housekeeping"],
    relatedSlugs: ["hm-ld-rolls", "collation-shrink-wrap"],
  },
  {
    slug: "pet-preforms",
    name: "PET Preforms",
    category: "Rigid Packaging & Closures",
    divisionId: "rigid-packaging",
    image: productImages.petPreforms,
    summary: "Precision preforms for dependable blow-moulding performance and lightweighting programs.",
    overview: "PET preforms engineered around consistent neck finish, weight distribution and repeatable conversion into bottles for water, beverages, food and industrial liquids.",
    specs: [{ label: "Neck finishes", value: "Standard / custom" }, { label: "Weight range", value: "8 – 90 g" }, { label: "Resin", value: "Food-grade PET" }, { label: "Tolerance", value: "Precision controlled" }],
    customization: ["Preform weight and length", "Neck finish and colour", "Lightweighting trials"],
    applications: ["Beverages", "Edible oils", "Personal care", "Industrial liquids"],
    relatedSlugs: ["closures-caps", "collation-shrink-wrap"],
  },
  {
    slug: "closures-caps",
    name: "Closures & Caps",
    category: "Rigid Packaging & Closures",
    divisionId: "rigid-packaging",
    image: productImages.closuresCaps,
    summary: "Secure, repeatable closures engineered for line speed, tamper evidence and fit.",
    overview: "A practical closure platform spanning standard and custom formats, tuned for sealing integrity, torque control and consistent integration with automated filling lines.",
    specs: [{ label: "Formats", value: "ROPP / snap-on / screw" }, { label: "Material", value: "PP / HDPE" }, { label: "Tamper evidence", value: "Band / induction-ready" }, { label: "Colour", value: "Custom masterbatch" }],
    customization: ["Thread and neck compatibility", "Colour matching", "Liner and seal options", "Embossed identification"],
    applications: ["Beverage bottles", "Agrochemicals", "Home care", "Industrial containers"],
    relatedSlugs: ["pet-preforms", "hm-ld-rolls"],
  },
];

export const chapters = [
  { number: "01", title: "Start with the melt", subtitle: "Polymer integrity", text: "Material consistency is the first production control. Resin, additive and process discipline set the ceiling for every downstream product.", metric: "1200KMT", label: "Materials processed each month" },
  { number: "02", title: "Build the structure", subtitle: "Tensile architecture", text: "We engineer the weave, film and wall geometry around the load a product will actually see in the field.", metric: "55 kN/m", label: "Peak tensile force tolerance" },
  { number: "03", title: "Protect the barrier", subtitle: "Multi-layer performance", text: "Moisture, oxygen and impact are not assumptions. They are parameters to be measured, documented and controlled.", metric: "<0.5 cc", label: "Oxygen transmission target" },
  { number: "04", title: "Contain the force", subtitle: "Rigid reliability", text: "Packaging earns trust when it survives handling, transport and storage without changing the promise inside.", metric: "2.2 m", label: "Certified drop test" },
  { number: "05", title: "Move at line speed", subtitle: "Bottling automation", text: "The final system is a synchronized one: filling, capping, coding and packing with no wasted motion between them.", metric: "350 BPM", label: "Peak automated throughput" },
];

export const industries = ["Agriculture & horticulture", "Construction & infrastructure", "Beverage & FMCG", "Chemicals & specialty materials", "Government & utilities", "Industrial manufacturing"];

export const stats = [
  { value: "100K", unit: "KG", label: "materials processed each month" },
  { value: "Pan India", unit: "Supply", label: "nationwide network" },
  { value: "5-7", unit: "%", label: "max gauge variance" },
  { value: "±0.02", unit: "mm", label: "tolerance compliance" },
];
